import React, { createContext, useCallback, useState, useEffect } from 'react';
import { SafeDatabaseUser } from '../types/types';
import { logoutUser } from '../services/userService';

interface LoginContextType {
  user: SafeDatabaseUser | null;
  loading: boolean;
  setUser: (user: SafeDatabaseUser | null) => void;
  logout: () => void;
}

const defaultContextValue: LoginContextType = {
  user: null,
  loading: true,
  setUser: () => {},
  logout: () => {},
};

const LoginContext = createContext<LoginContextType>(defaultContextValue);

const parseUserCookie = (): SafeDatabaseUser | null => {
  try {
    // First try to get user from cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.startsWith('fakeSO_user=')) {
        const encodedValue = trimmedCookie.substring('fakeSO_user='.length);
        if (encodedValue) {
          return JSON.parse(decodeURIComponent(encodedValue));
        }
      }
    }

    // If cookie not found, try localStorage as fallback
    const localUser = localStorage.getItem('fakeSO_user');
    if (localUser) {
      return JSON.parse(localUser);
    }
  } catch (error) {
    // Error parsing user cookie or localStorage, return nul
  }
  return null;
};

export const LoginProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<SafeDatabaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = parseUserCookie();
    if (userData) {
      setUserState(userData);
    }
    setLoading(false);
  }, []);

  const setUser = useCallback((newUser: SafeDatabaseUser | null) => {
    setUserState(newUser);
    // Also store in localStorage as a fallback mechanism
    if (newUser) {
      localStorage.setItem('fakeSO_user', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('fakeSO_user');
    }
    setLoading(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
      setUserState(null);
      localStorage.removeItem('fakeSO_user');
    } catch (error) {
      // Error during logout handled silently
      setUserState(null); // Still clear the user state to ensure logout occurs
      localStorage.removeItem('fakeSO_user');
    }
  }, []);

  return (
    <LoginContext.Provider value={{ user, loading, setUser, logout }}>
      {children}
    </LoginContext.Provider>
  );
};

export default LoginContext;
